import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FaEnvelope, FaTrash } from "react-icons/fa";
import { FaCirclePlus, FaGlobe } from "react-icons/fa6";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";
import ModalButton from "../../../../components/common/form/ModalButton";
import AddEditContactForm from "../../../../components/common/form/AddEditContactForm";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { useCreateContactMutation, useDeleteContactsByIdMutation, useGetContactByRelationIdQuery, useUpdateContactMutation } from "../../../../components/common/form/AddEditContactApi";


const Contact = () => {

    useEffect(() => {
        document.title = "Contacts"
    });

    const [deleteContacts, { isLoading }] = useDeleteContactsByIdMutation();
    const [contactData, setContactData] = useState<any>({});
    const [contactId, setContactId] = useState(null);

    const iconButtonStyle = "cursor-pointer  text-md  hover:text-highlight-7";

    const handleDelete = async (contactId: string) => {
        if (contactId) {
            await deleteContacts(contactId).unwrap();
        }
    };


    let { companyId } = useParams();

    const { data: collectorContactData } = useGetContactByRelationIdQuery(companyId);

    const [updateContactApi, updateContactApiResponse] = useUpdateContactMutation();
    const [createContactApi, createContactApiResponse] = useCreateContactMutation();
    const [show, setShow] = useState(false);

    const handleRowClick = (contactId: any) => {
        setContactId(contactId)
        setShow(true);
    };

    const handleSubmit = async (e: any) => {
        const isCreate = !contactId;

        const requestBody = {
            id: isCreate ? null : contactData?.id,
            name: contactData?.name,
            email: contactData?.email,
            website: contactData?.website,
            phone: contactData?.phone,
            fax: contactData?.fax,
            preferredContactMethod: contactData?.preferredContactMethod?.value || null,
            isPrimary: contactData?.isPrimary,
            note: contactData?.note,
            address: {
                ...contactData?.address,
                state: contactData?.address?.state?.value || null
            }
        };

        await handleRTKQuery(
            async () => {
                if (isCreate) {
                    return await createContactApi({
                        contactId: companyId,
                        requestBody,
                        isSendLink: false,
                    }).unwrap();
                } else {
                    return await updateContactApi({
                        contactId,
                        requestBody,
                    }).unwrap();
                }
            },
            (isSuccess: boolean) => {
                if (isSuccess) {
                    setContactData({});
                    setShow(false);
                }
            }
        );
    };


    return (
        <>
            <div className={"overflow-auto"}>
                <table className="w-full">
                    <thead className="bg-highlight-5 text-text-0">
                        <tr>
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Phone</th>
                            <th className="px-4 py-2 text-left">Email</th>
                            <th className="px-4 py-2 text-left">Website</th>
                            <th className="px-4 py-2 text-left">Address</th>
                            <th className="px-4 py-2 text-left">
                                {hasPermission(["MGTCOM-U"]) &&
                                    <ModalButton
                                        triggerButton={<FaCirclePlus className={iconButtonStyle}
                                            title={"Add Contact Form"} />}
                                        modal={
                                            <AddEditContactForm
                                                formType={"Add"}
                                                show={true}
                                                setShow={setShow}
                                                onSubmit={() => handleSubmit(contactData)}
                                                setContactData={setContactData}
                                                contactData={contactData}
                                                isLoading={createContactApiResponse?.isLoading}
                                                overrideDismissMethod={() => {
                                                    setContactData({});
                                                }}
                                                isReadOnly={hasPermission(["MGTCOM-U"]) === false}
                                            />
                                        }
                                    />}
                            </th>
                        </tr>
                    </thead>

                    <tbody className="table-striped text-sm">
                        {collectorContactData?.length === 0 ? (
                            <tr>
                                <td colSpan={17} className="bg-text-1  p-2">No data available</td>
                            </tr>
                        ) : (
                            collectorContactData?.map((row: any) => (
                                <tr key={row.key} className="odd:bg-text-1 hover:bg-text-1 transition cursor-pointer">

                                    <td
                                        className="p-2  text-highlight-0 cursor-pointer text-highlight-4"
                                        onClick={() => handleRowClick(row.id)}>
                                        {row.name}
                                    </td>
                                    <td className="px-4 py-2">{row?.phone}</td>
                                    <td className="px-4 py-2">
                                        {row?.email && <FaEnvelope title={row?.email} />}
                                    </td>
                                    <td className="px-4 py-2">
                                        {row?.website ? (
                                            <a
                                                href={row.website.startsWith("http") ? row.website : `https://${row.website}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 text-highlight-0 hover:underline"
                                            >
                                                <FaGlobe className="text-sm" />

                                            </a>
                                        ) : "-"}
                                    </td>
                                    <td className="px-4 py-2">{row?.address?.mailingAddress}</td>
                                    <td className="px-4 py-2">
                                        <ModalButton
                                            triggerButton={<FaTrash className={`${iconButtonStyle} !text-highlight-0`}
                                                title={"Delete Note"} />}
                                            modal={
                                                <ConfirmationModal
                                                    show={true}
                                                    setShow={() => {
                                                    }}
                                                    title={"Delete Contact"}
                                                    bodyMessage={"Are you sure you want to delete this contact?"}
                                                    onSubmit={() => handleDelete(row.id)}
                                                    isLoading={isLoading}
                                                />
                                            }
                                        />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>

                </table>

                {show && (
                    <AddEditContactForm
                        formType={"Update"}
                        show={true}
                        setShow={setShow}
                        contactId={contactId}
                        setContactData={setContactData}
                        contactData={contactData}
                        onSubmit={() => handleSubmit(contactData)}
                        isLoading={updateContactApiResponse.isLoading}
                        isReadOnly={hasPermission(["MGTCOM-U"]) === false}
                        overrideDismissMethod={() => {
                            setContactId(null);
                            setContactData({});
                        }}
                    />
                )}
            </div>


        </>

    );
}

export default Contact;